import { Fragment, type ReactNode } from 'react';
import { Text } from '@belong/ui';
import { PhinAvatarLazy } from '@belong/ai-pointer';
import {
  IconAssignedPro,
  IconEquipment,
  IconExclusions,
  IconVerifiedBadge,
  IconWarranty,
} from '@belong/icons';
import clsx from 'clsx';
import type { MaintenanceQuoteConsumerModel } from 'maintenance/types/quote-centric';
import { STRINGS } from 'maintenance/strings';

type QuoteScopeOfWorkSectionsProps = {
  details: NonNullable<MaintenanceQuoteConsumerModel['details']>;
};

export const LEADING = 'leading-[normal]';

const SectionHeading = ({
  icon,
  heading,
}: {
  icon: ReactNode;
  heading: string;
}) => (
  <div className="flex items-center gap-xs">
    {icon}
    <Text fontWeight="semibold" variant="h3" className={LEADING}>
      {heading}
    </Text>
  </div>
);

export const AssignedProSection = ({
  name,
  className,
}: {
  name: string;
  className?: string;
}) => (
  <div className={clsx('flex flex-col gap-sm', className)}>
    <SectionHeading
      icon={<IconAssignedPro width={24} height={24} />}
      heading={STRINGS['quote-assigned-pro']}
    />
    <div className="flex items-center gap-xs">
      <Text className={LEADING}>{name}</Text>
      <IconVerifiedBadge width={20} height={20} />
    </div>
  </div>
);

const SubHeading = ({ children }: { children: ReactNode }) => (
  <Text fontWeight="semibold" className={LEADING}>
    {children}
  </Text>
);

const ItemList = ({ items }: { items: string[] }) => (
  <div className="flex flex-col gap-xs">
    {items.map((item, index) => (
      <Text key={index} className={LEADING}>
        {item}
      </Text>
    ))}
  </div>
);

export const QuoteScopeOfWorkSections = ({
  details,
}: QuoteScopeOfWorkSectionsProps) => {
  const scopeSummary = details.scopeSummary || null;
  const workIncluded =
    details.workIncluded && details.workIncluded.length > 0
      ? details.workIncluded
      : null;
  const showScopeOfWork = !!scopeSummary || !!workIncluded;

  const equipment = details.equipment?.isVisibleToMember
    ? details.equipment
    : null;
  const equipmentFields = [
    {
      label: STRINGS['quote-existing-equipment'],
      value: equipment?.existingEquipment,
    },
    {
      label: STRINGS['quote-new-equipment'],
      value: equipment?.proposedEquipment,
    },
    {
      label: STRINGS['quote-specifications'],
      value: equipment?.specifications,
    },
  ].filter((field): field is { label: string; value: string } => !!field.value);
  const exclusionItems = details.exclusions?.isVisibleToMember
    ? (details.exclusions.items ?? [])
    : [];

  const warranty = details.warranty?.isVisibleToMember
    ? details.warranty.text
    : null;

  if (
    !showScopeOfWork &&
    equipmentFields.length === 0 &&
    exclusionItems.length === 0 &&
    !warranty
  ) {
    return null;
  }

  return (
    <div className="mt-xl flex flex-col gap-xl">
      {showScopeOfWork && (
        <div className="flex flex-col gap-sm">
          <SectionHeading
            icon={
              <div className="pointer-events-none flex h-md w-md shrink-0 items-center justify-center">
                <PhinAvatarLazy size={24} idleJumps={false} />
              </div>
            }
            heading={STRINGS['quote-scope-of-work']}
          />
          {scopeSummary && (
            <div className="flex flex-col gap-xs">
              <SubHeading>{STRINGS['quote-summary']}</SubHeading>
              <Text className={LEADING}>{scopeSummary}</Text>
            </div>
          )}
          {workIncluded && (
            <div className="flex flex-col gap-xs">
              <SubHeading>{STRINGS['quote-work-included']}</SubHeading>
              <ItemList items={workIncluded} />
            </div>
          )}
        </div>
      )}

      {equipmentFields.length > 0 && (
        <div className="flex flex-col gap-sm">
          <SectionHeading
            icon={<IconEquipment width={24} height={24} />}
            heading={STRINGS['quote-equipment']}
          />
          {equipmentFields.map(({ label, value }) => (
            <Fragment key={label}>
              <SubHeading>{label}</SubHeading>
              <Text className={LEADING}>{value}</Text>
            </Fragment>
          ))}
        </div>
      )}

      {exclusionItems.length > 0 && (
        <div className="flex flex-col gap-sm">
          <SectionHeading
            icon={<IconExclusions width={24} height={24} />}
            heading={STRINGS['quote-exclusions']}
          />
          <ItemList items={exclusionItems} />
        </div>
      )}

      {warranty && (
        <div className="flex flex-col gap-xs">
          <SectionHeading
            icon={<IconWarranty width={24} height={24} />}
            heading={STRINGS['quote-warranty']}
          />
          <Text className={LEADING}>{warranty}</Text>
        </div>
      )}
    </div>
  );
};
